import { useEffect, useState } from 'react';
import type { FeatureKey } from '@boardflight/shared';
import { api, loadSession, onSessionChange } from './api';

/**
 * What the signed-in account may do as itself, with no team in the question — how the by-hand
 * install and the assistant are asked (`docs/plans-and-features.md` §3).
 *
 * `features.tsx` is the dashboard's: what a person has in a team, read from the team list every
 * dashboard page already has. This is the other question — what the person's own tier brings, with
 * no team's plan in it — and the library has no team list to read it from, so it asks the account's
 * own record once (`GET /v1/me`, which lists the keys that are on and never the ones that are not).
 *
 * **It answers "no" until it knows "yes"**, and "no" for nobody signed in. That is the dashboard's
 * rule for a capability whose existence is not advertised (`CLAUDE.md`, plans are invisible): a
 * control is drawn when the key is there, and nothing at all is drawn — not a disabled one, not a
 * placeholder — when it is not, or not yet.
 */

/** One read for the page's life per account: a tier changed a minute ago is a reload away. */
let asked: { userId: string; answer: Promise<ReadonlySet<string>> } | null = null;
/** What the last read answered, kept so a page drawn after it starts with the answer, not without. */
let known: { userId: string | null; features: ReadonlySet<string> } | null = null;

function signedInAs(): string | null {
  return loadSession()?.userId ?? null;
}

function accountFeatures(): Promise<ReadonlySet<string>> {
  const userId = signedInAs();
  if (userId === null) return Promise.resolve(new Set());
  if (asked?.userId !== userId) {
    const read = api<{ features?: string[] }>('/v1/me').then(
      (me) => {
        const features = new Set(me.features ?? []);
        known = { userId, features };
        return features;
      },
      () => {
        // Not an answer, and not worth keeping: the next page asks again.
        if (asked?.answer === read) asked = null;
        return new Set<string>();
      },
    );
    asked = { userId, answer: read };
  }
  return asked.answer;
}

/**
 * Say what an account's own keys are without asking — for a test, which draws a page once and has
 * no effects to wait for. With no `features` it forgets everything, as a sign-out would.
 */
export function rememberAccountFeatures(features?: readonly string[]): void {
  asked = null;
  known = features === undefined ? null : { userId: signedInAs(), features: new Set(features) };
}

export function useAccountFeature(key: FeatureKey): boolean {
  const [on, setOn] = useState(() => known !== null && known.userId === signedInAs() && known.features.has(key));
  useEffect(() => {
    let live = true;
    const read = () =>
      void accountFeatures().then((features) => {
        if (live) setOn(features.has(key));
      });
    read();
    // Signing in or out is another account, or none.
    const stop = onSessionChange(read);
    return () => {
      live = false;
      stop();
    };
  }, [key]);
  return on;
}
